import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { apiClient } from '@/lib/apiClient';
import { ErrorService } from '@/lib/errors/ErrorService';
import type { CabinetMember } from '@/hooks/cabinet/types';

/**
 * Single source of truth for the current user's cabinet membership data.
 *
 * Replaces 5+ independent hooks that each called GET /cabinet-members/me
 * with different query keys. All consumers should derive from this cache.
 *
 * Polls at 5s intervals only when cabinet_id is not yet resolved
 * (new user during onboarding). Stops polling once resolved.
 */

export const CABINET_MEMBER_ME_KEY = 'cabinet-member-me';

export function useCabinetMemberMe() {
  const { user } = useAuth();

  const isOnProfileCompletionPage = window.location.pathname === '/complete-profile';

  return useQuery<CabinetMember | null>({
    queryKey: [CABINET_MEMBER_ME_KEY, user?.id],
    queryFn: async () => {
      if (!user) return null;
      try {
        return await apiClient.get<CabinetMember>('/cabinet-members/me');
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_cabinet_member_me' }, silent: true });
        throw error;
      }
    },
    enabled: !!user && !isOnProfileCompletionPage,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.cabinet_id) return false;
      // New user without cabinet — poll until resolved
      return 5_000;
    },
    // ⚠️ Contrat porteur — BackendHealthGate dépend de ce nombre de retries : il
    // définit le seuil avant l'affichage plein écran « serveur injoignable »
    // (5 tentatives, backoff cap 30s ≈ 31s d'échec soutenu). Le baisser ferait
    // apparaître la page sur un simple blip transitoire (ex: cold start Render 502).
    // Voir components/BackendHealthGate.tsx + backendHealth.ts.
    retry: (failureCount) => failureCount < 5,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/** Selector: returns the current user's cabinet_id, or null if not yet resolved. */
export function useCabinetId(): string | null {
  const { data } = useCabinetMemberMe();
  return data?.cabinet_id ?? null;
}

/** Selector: returns whether the current user is a cabinet admin. */
export function useIsAdmin(): boolean {
  const { data } = useCabinetMemberMe();
  return data?.cabinet_role === 'admin';
}

/** Selector: returns whether the current user is license-exempt (bypasses paywall). */
export function useLicenseExempt(): boolean {
  const { data } = useCabinetMemberMe();
  return data?.license_exempt ?? false;
}

/** Selector: ids d'annonces produit déjà vues par ce membre. */
export function useSeenAnnouncements(): string[] {
  const { data } = useCabinetMemberMe();
  return data?.seen_announcements ?? [];
}

/** Selector: returns the current user's platform_role, or null if not yet resolved. */
export function usePlatformRole(): 'customer' | 'hector_staff' | 'hector_super_admin' | null {
  const { data } = useCabinetMemberMe();
  return data?.platform_role ?? null;
}

/**
 * L'avocat connecté change son nom complet (`PATCH /cabinet-members/me/full-name`).
 *
 * L'invalidation de `/me` suffit à Paramètres, à l'avatar et à la navigation mobile.
 * WHY le rafraîchissement de session : Crisp lit `user.user_metadata.full_name` du jeton
 * Supabase, que le serveur vient de recopier ; sans nouveau jeton il garderait l'ancien nom.
 */
export function useUpdateMyFullName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fullName: string) =>
      apiClient.patch<{ fullName: string }>('/cabinet-members/me/full-name', { fullName }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [CABINET_MEMBER_ME_KEY] });
      queryClient.invalidateQueries({ queryKey: ['cabinet-members'] });
      await supabase.auth.refreshSession();
    },
  });
}
